/**
 * Training Artifact Types
 * 
 * Provider-agnostic metadata structure for learning resources.
 * No content hosting - all training links externally.
 */

export type ArtifactType = 
  | 'short_video' 
  | 'long_video' 
  | 'course_module' 
  | 'book' 
  | 'chapter'
  | 'ebook'
  | 'live_seminar' 
  | 'webinar'
  | 'hands_on_lab'
  | 'article'
  | 'podcast';

export type SkillDepth = 'foundation' | 'applied' | 'advanced' | 'specialist';
export type DeliveryFormat = 'on_demand' | 'live';
export type AccessType = 'free' | 'paid' | 'subscription' | 'employer_provided';
export type ArtifactScope = 'generic' | 'job_specific' | 'domain_foundation' | 'mastery';
export type ConfidenceLevel = 'provisional' | 'community_validated' | 'optibay_verified';
export type LearningIntent = 'curiosity' | 'job_help' | 'skill_growth' | 'required';
export type LearningSource = 'learning_center' | 'ticket_context' | 'recommendation';

// Sidebar mode for "Help with this Job" panel (replaces intent in sidebar path)
export type SidebarMode = 'job_specific' | 'foundational';

// Learning style types
export type LearningStyle = 'visual' | 'auditory' | 'read_write' | 'hands_on';
export type LearningConstraint = 'short_sessions' | 'mobile_only' | 'audio_ok' | 'travel_ok' | 'no_live';

// =============================================
// CANONICAL TRAINING EVENT TAXONOMY
// =============================================
// SINGLE SOURCE OF TRUTH: Every UI path must use exactly these event types.
// This replaces fragmented: opened|completed|abandoned|rated|bookmarked|consumption_started|consumption_confirmed

/**
 * CanonicalTrainingEventType
 * 
 * Unified event taxonomy for all training telemetry.
 * - impressed: artifact card visible in viewport (>=50% visible for >=500ms)
 * - opened: external link clicked, new tab opened
 * - engaged: meaningful return: >=30s on dialog OR >=1 reflection OR confirms partial/complete
 * - completed: completion confirmed via dialog
 * - abandoned: session timeout (>24h) OR explicit dismiss
 * - rated: feedback submitted (rating + confidence delta)
 */
export type CanonicalTrainingEventType =
  | 'impressed'
  | 'opened'
  | 'engaged'
  | 'completed'
  | 'abandoned'
  | 'rated';

// Deprecated alias for migration period - use CanonicalTrainingEventType instead
export type EffectivenessEventType = CanonicalTrainingEventType;

// Event surface types - where the event originated
export type TrainingEventSurface = 'foundational' | 'browse' | 'ticket_sidebar';

// Event source types - learning center context
export type TrainingEventSource = 'learning_center' | 'ticket_context' | 'schedule_aware';

// Confidence tiers for multi-signal verification
export type CompletionConfidenceTier = 'verified' | 'likely' | 'suspicious';

// Requirement status types
export type RequirementStatus = 'assigned' | 'in_progress' | 'completed' | 'overdue' | 'waived';

// Weight types for adaptive recommendations
export type WeightType = 'style' | 'provider' | 'artifact_type' | 'scope';

export interface TrainingArtifact {
  id: string;
  title: string;
  provider_name: string;
  instructor_name?: string | null;
  artifact_type: ArtifactType;
  access_url: string | null; // null for books without preview
  access_type: AccessType;
  estimated_time_minutes?: number | null;
  primary_skill?: string | null;
  skill_depth: SkillDepth;
  teaching_styles: string[];
  delivery_format: DeliveryFormat;
  travel_required: boolean;
  artifact_scope: ArtifactScope;
  confidence_level: ConfidenceLevel;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Book purchase fields
  purchase_url?: string | null; // Official product page URL (e.g., AESwave)
  purchase_provider_name?: string | null; // Seller name (e.g., "AESwave")
  is_reference_only?: boolean; // True if not publicly sold
  metadata_url?: string | null; // Internal reference (e.g., Google Books) - not user-facing
  isbn_13?: string | null;
  isbn_10?: string | null;
  // Summary fields
  original_summary_short?: string | null;
  summary_source?: 'ai_draft' | 'manual' | null; // Source of the summary
  // Vehicle/Notes-Aware Filtering (Phase 5)
  vehicle_fuel_types?: string[] | null;  // gas, diesel, hybrid, ev, any - NULL = applies to all
  dtc_codes?: string[] | null;           // P0016, P0301, etc. - NULL = no DTC filtering
  keywords?: string[] | null;            // misfire, rough idle, etc. - NULL = no keyword filtering
  // Book/Chapter hierarchy (MVP 1)
  parent_artifact_id?: string | null;
  sort_index?: number | null;
  authors?: string | null;
  publisher?: string | null;
  published_year?: number | null;
  edition?: string | null;
  mapping_status?: 'draft' | 'partial' | 'complete';
}

export interface TrainingArtifactDomainMap {
  id: string;
  artifact_id: string;
  domain_key: string;
  is_primary: boolean;
  relevance_score: number;
  created_at: string;
}

/**
 * Training Artifact Job Map
 * Links artifacts to specific jobs from jobs_catalog for job-specific training
 */
export interface TrainingArtifactJobMap {
  id: string;
  artifact_id: string;
  job_id: string; // FK to jobs_catalog(id)
  relevance_weight: number;
  created_at: string;
}

/**
 * Training Artifact Fitment
 * Links artifacts to specific vehicle year/make/model for vehicle-specific recommendations
 */
export interface TrainingArtifactFitment {
  id: string;
  artifact_id: string;
  year: number | null;
  make: string | null;
  model: string | null;
  engine: string | null;
  confidence: 'explicit' | 'inferred';
  boost: number;
  created_at: string;
  updated_at: string;
}

/**
 * Job Adjacency Map
 * Defines related jobs for adjacency scoring (e.g., fuel pump → fuel tank)
 */
export interface JobAdjacencyMap {
  id: string;
  source_job_id: string;
  related_job_id: string;
  boost: number;
  relationship_type: 'related' | 'prerequisite' | 'followup';
  created_at: string;
}

export interface LearningHistoryEntry {
  id: string;
  technician_profile_id: string;
  artifact_id: string;
  clicked_at: string;
  source: LearningSource;
  intent?: LearningIntent | null;
  ticket_id?: string | null;
  job_category?: string | null;
}

// Extended type with joined artifact data
export interface LearningHistoryWithArtifact extends LearningHistoryEntry {
  artifact: TrainingArtifact;
  /** Core-100 R4 CORE-O: opened / engaged / progressed / completed kept apart, with how completion was determined. */
  stages?: import('@/lib/training/learningHistoryStages').LearningHistoryStages;
  /** Core-100 R4 CORE-O: false when the training has since been retired (history keeps it, labelled). */
  artifact_is_current?: boolean | null;
}

// Extended type with domain mappings
export interface TrainingArtifactWithDomains extends TrainingArtifact {
  domains: TrainingArtifactDomainMap[];
  primary_domain?: string;
}

// =============================================
// CATALYST INTELLIGENCE TYPES
// =============================================

/**
 * Technician Learning Profile
 * Stores learning preferences, constraints, and inferred behaviors
 */
export interface TechnicianLearningProfile {
  technician_profile_id: string;
  preferred_styles: LearningStyle[];
  constraints: LearningConstraint[];
  current_level: SkillDepth;
  focus_domains: string[];
  inferred_styles: Record<LearningStyle, number>; // weights derived from behavior
  inferred_confidence: Record<string, number>; // confidence by domain
  created_at: string;
  updated_at: string;
}

/**
 * Training Effectiveness Events
 * Tracks all learning interactions for outcome measurement
 */
export interface TrainingEffectivenessEvent {
  id: string;
  technician_profile_id: string;
  artifact_id: string;
  source: LearningSource;
  ticket_id?: string | null;
  job_category?: string | null;
  event_type: EffectivenessEventType;
  rating?: number | null; // 1-5
  confidence_delta?: number | null; // -2 to +2
  time_spent_seconds?: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

/**
 * Training Job Outcomes
 * Correlates training exposure to job performance
 */
export interface TrainingJobOutcome {
  id: string;
  technician_profile_id: string;
  artifact_id?: string | null;
  domain_key: string;
  job_type_key?: string | null;
  ticket_id?: string | null;
  window_start_at?: string | null;
  window_end_at?: string | null;
  before_after_flag: 'before_training' | 'after_training';
  outcome_metrics: {
    actual_minutes?: number;
    estimated_minutes?: number;
    variance_minutes?: number;
    comeback_flag?: boolean;
    completion_success?: boolean;
  };
  created_at: string;
}

/**
 * Technician Training Weights
 * Adaptive recommendation weights per technician (normalized structure)
 */
export interface TechnicianTrainingWeight {
  id: string;
  technician_profile_id: string;
  domain_key: string;
  weight_type: WeightType;
  weight_key: string; // "hands_on", "ScannerDanner", "short_video"
  weight_value: number;
  samples_count: number;
  updated_at: string;
}

/**
 * Training Requirements
 * Shop-level training mandates and certifications
 */
export interface TrainingRequirement {
  id: string;
  org_id: string;
  team_id?: string | null;
  role_key?: string | null;
  domain_key?: string | null;
  artifact_id?: string | null;
  requirement_rule?: Record<string, unknown> | null;
  required_by_date?: string | null;
  created_by?: string | null;
  is_active: boolean;
  requires_manager_verification?: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  artifact?: TrainingArtifact;
}

/**
 * Technician Requirement Status
 * Tracks individual tech progress on requirements
 */
export interface TechnicianRequirementStatus {
  id: string;
  requirement_id: string;
  technician_profile_id: string;
  status: RequirementStatus;
  completed_at?: string | null;
  waived_reason?: string | null;
  waived_by?: string | null;
  updated_at: string;
  // Joined data
  requirement?: TrainingRequirement;
}

// Helper type for artifact type display
export const ARTIFACT_TYPE_LABELS: Record<ArtifactType, string> = {
  short_video: 'Short Video',
  long_video: 'Video Course',
  course_module: 'Course Module',
  book: 'Book',
  chapter: 'Chapter',
  ebook: 'eBook',
  live_seminar: 'Live Seminar',
  webinar: 'Webinar',
  hands_on_lab: 'Hands-On Lab',
  article: 'Article',
  podcast: 'Podcast',
};

export const ARTIFACT_TYPE_ICONS: Record<ArtifactType, string> = {
  short_video: '🎬',
  long_video: '📺',
  course_module: '📚',
  book: '📖',
  chapter: '🔖',
  ebook: '📱',
  live_seminar: '🎤',
  webinar: '💻',
  hands_on_lab: '🔧',
  article: '📄',
  podcast: '🎙️',
};

export const SKILL_DEPTH_LABELS: Record<SkillDepth, string> = {
  foundation: 'Foundation',
  applied: 'Applied',
  advanced: 'Advanced',
  specialist: 'Specialist',
};

export const ACCESS_TYPE_LABELS: Record<AccessType, string> = {
  free: 'Free',
  paid: 'Paid',
  subscription: 'Subscription',
  employer_provided: 'Employer Provided',
};

export const CONFIDENCE_LEVEL_LABELS: Record<ConfidenceLevel, string> = {
  provisional: 'Provisional',
  community_validated: 'Community Validated',
  // OD-35 (2026-10-15): a content-confidence tier OptiBay reviewed — never an unqualified "Verified".
  optibay_verified: 'OptiBay reviewed content',
};

export const LEARNING_STYLE_LABELS: Record<LearningStyle, string> = {
  visual: 'Visual',
  auditory: 'Auditory',
  read_write: 'Reading/Writing',
  hands_on: 'Hands-On',
};

export const LEARNING_CONSTRAINT_LABELS: Record<LearningConstraint, string> = {
  short_sessions: 'Short Sessions Only',
  mobile_only: 'Mobile-Friendly',
  audio_ok: 'Audio OK',
  travel_ok: 'Can Travel',
  no_live: 'No Live Sessions',
};

export const REQUIREMENT_STATUS_LABELS: Record<RequirementStatus, string> = {
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed',
  overdue: 'Overdue',
  waived: 'Waived',
};

// =============================================
// PHASE 3: LEARNING IMPACT LOOP TYPES
// Engagement signals and observed correlations
// =============================================

/**
 * Effect signal types - what we measure after training engagement
 */
export type EffectSignalType = 
  | 'reduced_comebacks' 
  | 'reduced_time_overruns' 
  | 'improved_first_time_fix' 
  | 'reduced_help_requests';

/**
 * Effect direction - correlation observed
 */
export type EffectDirection = 'positive' | 'neutral' | 'negative';

/**
 * Outcome window types for observation periods
 */
export type WindowType = '7d' | '14d' | '30d';

/**
 * Window status
 */
export type WindowStatus = 'active' | 'completed' | 'insufficient_data';

/**
 * Training Outcome Window
 * Observation window spawned after training engagement to compare pre/post performance
 * Self-to-self comparison only - never cross-technician
 */
export interface TrainingOutcomeWindow {
  id: string;
  technician_profile_id: string;
  artifact_id: string;
  domain_key: string;
  engagement_event_id?: string | null;
  window_type: WindowType;
  window_start_at: string;
  window_end_at: string;
  baseline_metrics: {
    comeback_rate?: number;
    avg_variance_minutes?: number;
    jobs_count?: number;
    diagnostic_accuracy?: number;
  };
  final_metrics?: Record<string, number> | null;
  comparison_result?: EffectDirection | null;
  status: WindowStatus;
  created_at: string;
  updated_at: string;
}

/**
 * Training Effect Signal
 * Aggregate correlation signal per artifact × domain
 * NOT per technician - this measures artifact impact, not people
 * 
 * Phase 3 Tweaks:
 * - TWEAK 2: Signals remain 'neutral' until sample_size >= 5
 * - TWEAK 3: Positive requires 60% favorable, negative requires 75% unfavorable
 */
export interface TrainingEffectSignal {
  id: string;
  artifact_id: string;
  domain_key: string;
  signal_type: EffectSignalType;
  direction: EffectDirection;
  confidence_weight: number; // -1 to 1
  sample_size: number;
  positive_samples: number;
  negative_samples: number;
  last_updated_at: string;
  decay_applied_at?: string;
}

/**
 * Safety constants for Phase 3
 * These enforce slow learning and asymmetric evidence requirements
 */
export const PHASE3_CONSTANTS = {
  /** Minimum samples before a signal can be non-neutral (Tweak 2) */
  MINIMUM_SAMPLE_THRESHOLD: 5,
  /** Threshold for positive direction (60% favorable) (Tweak 3) */
  POSITIVE_THRESHOLD: 0.60,
  /** Threshold for negative direction (75% unfavorable) (Tweak 3) */
  NEGATIVE_THRESHOLD: 0.75,
  /** Maximum influence on recommendations (capped at ±0.3) */
  MAX_RECOMMENDATION_INFLUENCE: 0.30,
  /** Decay factor applied to stale signals */
  DECAY_FACTOR: 0.98,
  /** Natural decay factor for confidence over time */
  NATURAL_DECAY: 0.995,
} as const;

// =============================================
// PHASE 4: PROVIDER EXPANSION FRAMEWORK TYPES
// Provider-agnostic scaling architecture
// =============================================

/**
 * Provider type classification
 */
export type ProviderType = 'educator' | 'publisher' | 'oem' | 'distributor' | 'platform';

/**
 * Delivery modes supported by a provider
 */
export type DeliveryMode = 'video' | 'book' | 'class' | 'webinar' | 'hands_on' | 'article';

/**
 * Visibility levels for providers
 * - internal_only: Only visible to OptiBay admins
 * - admin_only: Only visible to shop managers
 * - technician_visible: Visible to all users
 */
export type VisibilityLevel = 'internal_only' | 'admin_only' | 'technician_visible';

/**
 * Trust level (admin-facing label)
 * Note: trust_score (numeric) is internal-only and never exposed to UI
 */
export type TrustLevel = 'low' | 'medium' | 'high';

/**
 * Provider lifecycle states
 */
export type ProviderLifecycleStatus = 
  | 'created' 
  | 'active' 
  | 'paused' 
  | 'hidden' 
  | 'takedown_requested' 
  | 'disabled';

/**
 * Source types for provider discovery
 */
export type ProviderSourceType = 
  | 'youtube_channel' 
  | 'youtube_playlist' 
  | 'google_books' 
  | 'calendar_url' 
  | 'rss_feed' 
  | 'json_api'
  | 'manual';

/**
 * Audit log action types
 */
export type ProviderAuditAction =
  | 'created'
  | 'updated'
  | 'paused'
  | 'resumed'
  | 'hidden'
  | 'unhidden'
  | 'takedown_requested'
  | 'takedown_completed'
  | 'disabled'
  | 'enabled'
  | 'learning_paused'
  | 'learning_resumed'
  | 'source_added'
  | 'source_removed'
  | 'discovery_enabled'
  | 'discovery_disabled'
  | 'visibility_changed';

/**
 * Extended Training Provider
 * Phase 4 expansion with full lifecycle and control fields
 */
export interface TrainingProviderExtended {
  id: string;
  name: string;
  display_name: string;
  website_url?: string | null;
  description?: string | null;
  logo_url?: string | null;
  trust_score: number; // Internal-only, never surface to UI
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Phase 4 extensions
  provider_type: ProviderType;
  delivery_modes: DeliveryMode[];
  visibility_level: VisibilityLevel;
  trust_level: TrustLevel; // Admin-facing label
  requires_manual_review: boolean;
  discovery_enabled: boolean;
  discovery_paused_at?: string | null;
  hidden_from_recommendations: boolean;
  takedown_reason?: string | null;
  takedown_requested_at?: string | null;
  takedown_requested_by?: string | null;
  provider_metadata: Record<string, unknown>;
  last_audit_at?: string | null;
  audit_notes?: string | null;
  learning_enabled: boolean; // Tweak #1: Surgical control
  lifecycle_status: ProviderLifecycleStatus;
  // Ecosystem extensions (Phase 5)
  stable_key?: string | null;
  org_kind?: string | null;
  coverage?: string | null;
  parent_provider_id?: string | null;
  priority_tier?: number | null;
  canonical_domain?: string | null;
}

/**
 * Training Provider Source
 * Discovery source configuration per provider
 */
export interface TrainingProviderSource {
  id: string;
  provider_id: string;
  source_type: ProviderSourceType;
  source_identifier: string;
  display_name?: string | null;
  discovery_enabled: boolean;
  visibility_override?: 'internal_only' | 'admin_only' | null; // Tweak #3
  rate_limit_per_day?: number | null;
  last_fetched_at?: string | null;
  fetch_count: number;
  last_error?: string | null;
  source_metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Provider Audit Log Entry
 * Compliance tracking for all provider state changes
 */
export interface ProviderAuditLogEntry {
  id: string;
  provider_id: string;
  action: ProviderAuditAction;
  performed_by?: string | null;
  old_values?: Record<string, unknown> | null;
  new_values?: Record<string, unknown> | null;
  reason?: string | null;
  created_at: string;
}

/**
 * Provider manage action types for edge function
 */
export type ProviderManageAction =
  | 'update'
  | 'pause'
  | 'resume'
  | 'hide'
  | 'unhide'
  | 'request_takedown'
  | 'complete_takedown'
  | 'disable'
  | 'enable'
  | 'pause_learning'
  | 'resume_learning'
  | 'enable_discovery'
  | 'disable_discovery'
  | 'add_source'
  | 'update_source'
  | 'remove_source'
  | 'dry_run_discovery';

// Phase 4 Label mappings
export const PROVIDER_TYPE_LABELS: Record<ProviderType, string> = {
  educator: 'Educator',
  publisher: 'Publisher',
  oem: 'OEM',
  distributor: 'Distributor',
  platform: 'Platform',
};

export const VISIBILITY_LEVEL_LABELS: Record<VisibilityLevel, string> = {
  internal_only: 'Internal Only',
  admin_only: 'Admin Only',
  technician_visible: 'Visible to Technicians',
};

export const TRUST_LEVEL_LABELS: Record<TrustLevel, string> = {
  low: 'Low Trust',
  medium: 'Medium Trust',
  high: 'High Trust',
};

export const LIFECYCLE_STATUS_LABELS: Record<ProviderLifecycleStatus, string> = {
  created: 'Created',
  active: 'Active',
  paused: 'Paused',
  hidden: 'Hidden',
  takedown_requested: 'Takedown Requested',
  disabled: 'Disabled',
};

export const SOURCE_TYPE_LABELS: Record<ProviderSourceType, string> = {
  youtube_channel: 'YouTube Channel',
  youtube_playlist: 'YouTube Playlist',
  google_books: 'Google Books',
  calendar_url: 'Calendar URL',
  rss_feed: 'RSS Feed',
  json_api: 'JSON API',
  manual: 'Manual Entry',
};

// Re-export capability types from canonical source
export type {
  TrainingCapability,
  ArtifactCapabilityMapRow,
  CapabilityKey,
  CapabilityCluster,
  RoleTarget,
} from '@/constants/trainingCapabilities';
